import fetchDocumentTypes from '@Application/Documents/DocumentTypes/fetchDocumentTypes';
import FetchDocumentTypes from '@Application/Documents/DocumentTypes/Queries/FetchDocumentTypes';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

export type UseAccountDocumentTypesResult = {
	documentTypes: DocumentTypesListView | null;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
	accountId: number | null;
};

/**
 * Wraps `useStoreQuery` for the account-scoped document types catalogue, the same "no account, no
 * request" pattern as `AccountAiAssignmentsContent`: without an account, `query` never leaves
 * (`Promise.resolve()`), so `stopOn` reports "done" immediately instead of looping forever. The
 * caller shows a skeleton while `documentTypes === null && error === null && accountId !== null`, and
 * an error state only once `error !== null` — never deduced from a `null` list alone
 * (`state-stores.md`, "Ce que `useStoreQuery` ne garantit pas").
 */
function useAccountDocumentTypes(): UseAccountDocumentTypesResult {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const {
		data: documentTypes,
		isLoading,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.documentTypesStore.documentTypes,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchDocumentTypes(new FetchDocumentTypes(accountId)));
		},
		deps: [accountId],
	});

	return { documentTypes, isLoading, error, refetch, accountId };
}

export default useAccountDocumentTypes;
